import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import ScalesIcon from '../icons/IconLib/ScaleIcon'
import ShopBagIcon from '../icons/IconLib/ShoppingBagIcon'
import PackageIcon from '../icons/IconLib/PackageBoxIcon'
import AppleIcon from '../icons/IconLib/AppleIcon'
import XIcon from '../icons/IconLib/XIcon'
import { BasketProduct, BasketProps, StyledProps } from './types/basketTypes'
import { stylePackByID } from '../utils/stylePackByPackType'
import { checkPackType } from '../utils/checkPackType'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/reducers/rootReducer'
import { IPackagesItem } from '../../modules/API/Defaults/models/packages'
import KbdChipButton from '../ui/KbdChipButton/KbdChipButton'
import BasketItem from './BasketItem'

const Basket: React.FC<BasketProps> = ({
  products,
  quantities,
  isOpen,
  onClose,
  Remark,
}) => {
  const packageList: IPackagesItem[] = useSelector(
    (state: RootState) => state.defaults.packageList,
  )
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null)
  const [isGlobalRemarkExpanded, setIsGlobalRemarkExpanded] = useState<boolean>(false)

  const handleProductClick = (productId: string) => {
    setExpandedProductId(expandedProductId === productId ? null : productId)
  }

  const isGlobalRemarkMultiLine = useMemo(() => {
    if (!Remark) return false
    return Remark.includes('\n') || Remark.length > 80
  }, [Remark])

  const handleGlobalRemarkClick = () => {
    if (isGlobalRemarkMultiLine) {
      setIsGlobalRemarkExpanded(!isGlobalRemarkExpanded)
    }
  }

  const calculateInnerQty = (product: BasketProduct) => {
    const packType: string = checkPackType(packageList, product.PackID)
    const tempPackWeight =
      product?.AvailablePacks?.filter((e) => e.PackID === product.PackID)[0]?.PackWeight ||
      product.PackWeight
    let kgInOnePack = 0
    if (product.OrderPacks && product.OrderWeight) {
      kgInOnePack = product.OrderWeight / product.OrderPacks
    } else {
      kgInOnePack = tempPackWeight || 0
    }

    if (packType === '2' || packType === '1') {
      return product.Packs
    } else {
      return kgInOnePack * product.Packs
    }
  }

  const calculateFinalProductPrice = (product: BasketProduct) => {
    let price = 0
    if (product.Price) price = product.Price
    else if (product.SalePrice) price = product.SalePrice
    else if (product.PriceGood) price = product.PriceGood
    return {
      totalPrice: price * calculateInnerQty(product),
      price,
    }
  }

  const { uniqueProductsCount, totalPrice } = useMemo(() => {
    return {
      uniqueProductsCount: products.length,
      totalPrice: products.reduce(
        (total, product) =>
          total +
          (product.Price || product.SalePrice || product.PriceGood || 0) *
            calculateInnerQty(product),
        0,
      ),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  const hasPackBreakdown =
    !!quantities && (quantities.weight > 0 || quantities.units > 0 || quantities.packages > 0)

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <Panel $isOpen={isOpen}>
        <Header>
          <Title>סל</Title>
          <KbdChipButton aria-label='close' onClick={onClose}>
            <XIcon />
          </KbdChipButton>
        </Header>

        {Remark && Remark.trim() !== '' && (
          <GlobalRemark>
            <RemarkText
              $expanded={isGlobalRemarkExpanded}
              $multiLine={isGlobalRemarkMultiLine}
              onClick={handleGlobalRemarkClick}
            >
              {Remark}
            </RemarkText>
          </GlobalRemark>
        )}

        <Content>
          {products.length === 0 ? (
            <EmptyState>
              <EmptyIcon><ShopBagIcon /></EmptyIcon>
              <EmptyTitle>הסל ריק</EmptyTitle>
              <EmptyHint>בחר מוצרים בהזמנה</EmptyHint>
            </EmptyState>
          ) : (
            products.map((product) => {
              const key = `${product.ProductID}-${product.VarietyID}-${product.SizeID}`
              const packTypeStyle = stylePackByID(product.packType)
              const isExpanded = expandedProductId === key
              const productPrice = calculateFinalProductPrice(product)
              const innerWeight = calculateInnerQty(product)
              const weightPerPack =
                product?.AvailablePacks?.filter((e) => e.PackID === product.PackID)[0]
                  ?.PackWeight ||
                product.PackWeight ||
                (product?.OrderWeight || 0) / (product.OrderPacks || 0) ||
                0
              return (
                <BasketItem
                  key={key}
                  product={product}
                  packTypeStyle={packTypeStyle}
                  pricePerUnit={productPrice.price}
                  totalProductPrice={productPrice.totalPrice}
                  innerWeight={innerWeight}
                  weightPerPack={weightPerPack}
                  isExpanded={isExpanded}
                  onToggle={() => handleProductClick(key)}
                />
              )
            })
          )}
        </Content>

        <Footer>
          <SummaryGrid>
            <SummaryCell $tone='accent'>
              <CellIcon $tone='accent'><AppleIcon /></CellIcon>
              <CellText>
                <CellTitle>פריטים</CellTitle>
                <CellValue>{uniqueProductsCount}</CellValue>
              </CellText>
            </SummaryCell>
            <SummaryCell $tone='warning'>
              <CellIcon $tone='warning'><PriceSym>₪</PriceSym></CellIcon>
              <CellText>
                <CellTitle>סכום</CellTitle>
                <CellValue>{totalPrice.toFixed(2)}</CellValue>
              </CellText>
            </SummaryCell>
            <SummaryCell $tone='success'>
              <CellIcon $tone='success'><ScalesIcon /></CellIcon>
              <CellText>
                <CellTitle>משקל</CellTitle>
                <CellValue>{(quantities?.totalWeight ?? 0).toFixed(1)}</CellValue>
              </CellText>
            </SummaryCell>
          </SummaryGrid>

          {hasPackBreakdown && (
            <BreakdownRow>
              {quantities!.weight > 0 && (
                <BreakdownChip>
                  <ScalesIcon />
                  <Mono>{quantities!.weight}</Mono>
                </BreakdownChip>
              )}
              {quantities!.units > 0 && (
                <BreakdownChip>
                  <ShopBagIcon />
                  <Mono>{quantities!.units}</Mono>
                </BreakdownChip>
              )}
              {quantities!.packages > 0 && (
                <BreakdownChip>
                  <PackageIcon />
                  <Mono>{quantities!.packages}</Mono>
                </BreakdownChip>
              )}
            </BreakdownRow>
          )}
        </Footer>
      </Panel>
    </>
  )
}

export default Basket

const Overlay = styled.div<StyledProps>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition:
    opacity ${({ theme }) => theme.motion.base} ease,
    visibility ${({ theme }) => theme.motion.base} ease;
  z-index: 999;
`

const Panel = styled.div<StyledProps>`
  position: fixed;
  top: 0;
  right: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
  width: 100%;
  /* 100vh on mobile includes the disappearing browser chrome (URL bar),
     so the footer falls below the visible viewport. 100dvh tracks the
     real visible area; 100vh stays as fallback for older browsers. */
  height: 100vh;
  height: 100dvh;
  background: ${({ theme }) => theme.colors.bg};
  box-shadow: -2px 0 16px rgba(0, 0, 0, 0.08);
  transition: right 280ms cubic-bezier(0.2, 0.8, 0.2, 1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  direction: rtl;

  @media (min-width: 768px) {
    width: 50%;
    max-width: 420px;
    min-width: 380px;
    right: ${({ $isOpen }) => ($isOpen ? '0' : '-420px')};
  }
`

const Header = styled.header`
  flex-shrink: 0;
  padding: 0.875rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
`

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.xl};
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.ink};
`

const GlobalRemark = styled.div`
  flex-shrink: 0;
  padding: 0.625rem 1rem;
  background: ${({ theme }) => theme.colors.accentSoft};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  border-inline-start: 3px solid ${({ theme }) => theme.colors.warning};
`

const RemarkText = styled.div<{ $expanded: boolean; $multiLine: boolean }>`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink2};
  line-height: 1.4;
  cursor: ${({ $multiLine }) => ($multiLine ? 'pointer' : 'default')};
  overflow: hidden;
  max-height: ${({ $expanded, $multiLine }) => ($expanded || !$multiLine ? '200px' : '1.4em')};
  transition: max-height ${({ theme }) => theme.motion.base} ease;
`

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Stop scroll from bleeding to <body> when reaching the edges
     (Android rubber-band effect that also produces phantom body scroll). */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &::-webkit-scrollbar {
    width: 0;
  }
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: ${({ theme }) => theme.colors.ink3};
  text-align: center;
`

const EmptyIcon = styled.div`
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.line2};
  color: ${({ theme }) => theme.colors.icon};

  svg { width: 1.25rem; height: 1.25rem; }
`

const EmptyTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink2};
`

const EmptyHint = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink4};
`

const Footer = styled.footer`
  flex-shrink: 0;
  padding: 0.75rem 0.875rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
`

type Tone = 'accent' | 'warning' | 'success'

const TONE_BG: Record<Tone, string> = {
  accent: 'rgba(197, 242, 48, 0.18)',
  warning: 'rgba(245, 158, 11, 0.16)',
  success: 'rgba(34, 197, 94, 0.16)',
}

const TONE_FG: Record<Tone, string> = {
  accent: '#5C7A0B',
  warning: '#B45309',
  success: '#15803D',
}

const SummaryCell = styled.div<{ $tone: Tone }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  background: ${({ $tone }) => TONE_BG[$tone]};
  border-radius: ${({ theme }) => theme.radii.md};
  direction: ltr;
  min-width: 0;
`

const CellIcon = styled.span<{ $tone: Tone }>`
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ $tone }) => TONE_FG[$tone]};
  flex-shrink: 0;

  svg { width: 0.95rem; height: 0.95rem; }
`

const PriceSym = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1;
`

const CellText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  align-items: flex-end;
  text-align: right;
  direction: rtl;
  min-width: 0;
  flex: 1;
`

const CellTitle = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.xs};
  color: ${({ theme }) => theme.colors.ink2};
  font-weight: 500;
  white-space: nowrap;
`

const CellValue = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-weight: 700;
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  white-space: nowrap;
`

const Mono = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-weight: 600;
  font-size: ${({ theme }) => theme.text.md};
`

const BreakdownRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  justify-content: flex-start;
`

const BreakdownChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.line2};
  color: ${({ theme }) => theme.colors.ink2};
  border-radius: ${({ theme }) => theme.radii.pill};
  font-size: ${({ theme }) => theme.text.xs};

  svg { width: 0.75rem; height: 0.75rem; color: ${({ theme }) => theme.colors.icon}; }
`
